import type { PartnerProfile } from "../../services/produktpartnerService";
// persistRelations.ts — schreibt die gesammelten Verknüpfungen ERST beim
// Speichern des Formulars (nie vorher). Pro Eintrag:
//   1. frei eingetippter Gegenkontakt → JETZT POST /contact (mit geerbter Ownership)
//   2. POST /contact-relation in der richtigen Richtung + kind-spezifischen Feldern
// Ein fehlerhafter Eintrag bricht die (bereits angelegte) Hauptanlage NICHT ab.

import { apiUrl } from "../../utils/api";
import { geschlechtAusAnrede } from "../../utils/anredeGeschlecht";
import type {
  ContactRef,
  RelationCollections,
  RelKind,
  GfgItem,
  VerantItem,
  FirmaConnItem,
  PersonFirmaItem,
  HouseholdItem,
  AssistantItem,
  StatusBezugItem,
  VertretungItem,
} from "./types";
import { refHasName, canonicalFirmaConn, weitereBranchenNormalisieren } from "./types";
import { JOINT_VENTURE, SCHWESTER } from "../../data/firmenverbindungen";
import { anteilsFakt, gleicheBeteiligung } from "../../data/anteilsbeziehung";
import { ASSISTENZ_GEGENROLLE, ASSISTENZ_ROLLE } from "../../data/privateAssistenz";
import { bezugKante, bezugZuRelation } from "../../data/statusBezugMapping";
import { vertretungsGegenrolle } from "../../data/vertretungsrollen";
import { buildPersonCommItems, type CommItem } from "../../utils/contactCommHelpers";
import { branchengruppenSpeichern } from "../../services/branchengruppeService";
import { logoUebernehmen } from "../../services/firmendatenService";
import { gemerktesLogo, logoVergessen } from "../../utils/logoVorschlag";
import { RELATIONSHIP_FREE } from "../../utils/householdRole";
import { foListeAusForm } from "../../utils/foUserIds";
import { apiFetch } from "../../utils/apiFetch";
import type { VerantwortlichkeitValue, VerantRole } from "../verantwortlichkeit/VerantwortlichkeitInput";

export type Ownership = {
  foUserId?: string;
  /**
   * Alle FO (28.09.2026), erster = `foUserId`. Unterkontakte bekommen dieselbe
   * Liste — wer den Hauptkontakt betreut, betreut auch seinen Haushalt.
   */
  foUserIds?: string[];
  boType?: string;
  boUserId?: string;
  boTeamId?: string;
  /** „Persönlicher Kontakt von …" — geht ebenfalls auf die Unterkontakte über. */
  inhouseContactUserId?: string;
};

/**
 * Verantwortlichkeit des Hauptkontakts aus dem Formularzustand — EINE Quelle
 * für alle Wege, auf denen Unterkontakte entstehen (Haushalt, Beziehungen,
 * Menschen fürs Unternehmen, Assistenz, Vertretungen, Firmen, Arbeitgeber,
 * Vereine). Vorher baute jede Stelle sich das Objekt selbst und ließ mehrere
 * FO und den persönlichen Inhouse-Kontakt weg (User 28.09.2026).
 */
export function ownershipAusFormular(formFields: Record<string, unknown>): Ownership {
  const t = (k: string) => (typeof formFields[k] === "string" ? (formFields[k] as string).trim() : "");
  // C8 (Plan 2026-09-30): Ein persönlicher Kontakt vererbt seine
  // Verantwortung nicht — seine Unterkontakte bekommen die Standard-FO.
  if (t("persoenlich") === "ja" || formFields.persoenlich === true) return {};
  const foUserIds = foListeAusForm(formFields);
  const foUserId = t("foUserId") || foUserIds[0] || "";
  return {
    ...(foUserId ? { foUserId } : {}),
    ...(foUserId && foUserIds.length > 1 && foUserIds[0] === foUserId ? { foUserIds } : {}),
    ...(t("boType") ? { boType: t("boType") } : {}),
    ...(t("boUserId") ? { boUserId: t("boUserId") } : {}),
    ...(t("boTeamId") ? { boTeamId: t("boTeamId") } : {}),
    ...(t("inhouseContactUserId") ? { inhouseContactUserId: t("inhouseContactUserId") } : {}),
  };
}

/** Die Verantwortlichkeits-Felder eines Unterkontakt-Payloads. */
export function ownershipPayload(ownership: Ownership): Record<string, unknown> {
  const fo = ownership.foUserId?.trim();
  const liste = (ownership.foUserIds ?? []).map((x) => x.trim()).filter(Boolean);
  return {
    ...(fo ? { foUserId: fo } : {}),
    // Nur eine Liste, die mit dem primären FO beginnt — sonst entschiede der
    // Server über eine Liste, die nicht zum mitgeschickten `foUserId` passt.
    ...(fo && liste.length > 1 && liste[0] === fo ? { foUserIds: [...new Set(liste)] } : {}),
    ...(ownership.boType ? { boType: ownership.boType } : {}),
    ...(ownership.boType === "USER" && ownership.boUserId ? { boUserId: ownership.boUserId } : {}),
    ...(ownership.boType === "TEAM" && ownership.boTeamId ? { boTeamId: ownership.boTeamId } : {}),
    ...(ownership.inhouseContactUserId ? { inhouseContactUserId: ownership.inhouseContactUserId } : {}),
  };
}

/**
 * Gemeinsame Adresse (Erst-/Wohnanschrift des Hauptkontakts), die neu angelegte
 * Haushaltsmitglieder erben. Nur gesetzte Teile werden übernommen.
 */
export type SharedAddress = {
  street?: string;
  postalCode?: string;
  city?: string;
  ortsteil?: string;
  country?: string;
};

// ── ContactType-ids (Person/Firma) einmal auflösen + cachen ──────────────────
let typeCache: { person?: number; firma?: number } | null = null;
async function resolveTypeIds(): Promise<{ person?: number; firma?: number }> {
  if (typeCache) return typeCache;
  try {
    const res = await fetch(apiUrl("contacttype"));
    const list = await res.json();
    const find = (n: string) =>
      Array.isArray(list) ? list.find((t: any) => String(t?.type).toLowerCase() === n)?.id : undefined;
    typeCache = { person: find("person"), firma: find("firma") };
  } catch {
    typeCache = {};
  }
  return typeCache;
}

type RelBody = {
  relationType: string;
  reverseRelationType?: string;
  function?: string;
  share?: string;
  isExtern?: boolean;
  sameHousehold?: boolean;
  /** „Person ist ausgeschieden?" (nur personFirma, User 28.09.2026). */
  ausgeschieden?: boolean;
  beginn?: string;
  ende?: string;
};

/**
 * Ausgeschieden-Felder eines Eintrags für den Rumpf — nur, wenn gesetzt. Ein
 * `false` fehlt bewusst: so bleiben die Rümpfe (und Fingerabdrücke) aller
 * übrigen Einträge unverändert. Das Zurücknehmen erledigt der PATCH, der das
 * Feld bei personFirma immer mitschickt.
 */
function ausgeschiedenRumpf(item: any): Pick<RelBody, "ausgeschieden" | "beginn" | "ende"> {
  const beginn = (item?.beginn ?? "").trim?.() ?? "";
  // Eintrittsdatum ohne Ausscheiden (User 28.09.2026): `false` mitschicken,
  // sonst übernähme das Backend den Beginn nicht.
  if (!item?.ausgeschieden) return beginn ? { ausgeschieden: false, beginn } : {};
  const ende = (item.ende ?? "").trim?.() ?? "";
  return { ausgeschieden: true, ...(beginn ? { beginn } : {}), ...(ende ? { ende } : {}) };
}

/** Volle VerantwortlichkeitInput-Wahl → eine RelBody je Rolle (Firma=src/Person=dest). */
function verantToRelBodies(v: VerantwortlichkeitValue | null): RelBody[] {
  if (!v) return [];
  const out: RelBody[] = [];
  for (const r of v.roles as VerantRole[]) {
    if (r.kind === "gf") {
      out.push({ relationType: "Geschäftsführer" });
    } else if (r.kind === "gesellschafter") {
      const body: RelBody = { relationType: "Gesellschafter" };
      if (r.istGeschaeftsfuehrer) body.function = "geschäftsführend";
      const sh = (r.share ?? "").trim();
      if (sh) body.share = sh;
      out.push(body);
    } else if (r.kind === "mensch") {
      out.push({ relationType: "Mensch fürs Unternehmen", function: r.verantwortlichkeit, isExtern: !!v.isExtern });
    }
  }
  return out;
}

// Übernimmt die geprüfte core()-Logik aus RelationCreateDialog, parametrisiert
// über die Item-Felder (statt Komponenten-State). Liefert i. d. R. genau eine
// Beziehung — personFirma (mehrere Verantwortlichkeit-Rollen) kann mehrere liefern.
/**
 * Die Backend-Zeilen, die ein Eintrag erzeugt.
 *
 * Exportiert, weil hier entschieden wird, was gespeichert wird und was
 * ersatzlos entfällt — eine Regel, die ohne Test nur im Kopf existiert.
 */
export function relationBodies(kind: RelKind, item: any): RelBody[] {
  const applyGfg = (role: string): RelBody => {
    const relationType = role === "Geschäftsführer und Gesellschafter" ? "Gesellschafter" : role;
    const out: RelBody = { relationType };
    if (role === "Geschäftsführer und Gesellschafter") out.function = "geschäftsführend";
    if (role !== "Geschäftsführer") {
      const sh = (item.share ?? "").trim?.() ?? "";
      if (sh) out.share = sh;
    }
    return out;
  };

  if (kind === "personFirma") {
    const aus = ausgeschiedenRumpf(item);
    return verantToRelBodies(item.verant).map((b) => ({ ...b, ...aus }));
  }
  if (kind === "gfg") return item.role ? [applyGfg(item.role)] : [];
  if (kind === "verant") {
    if (!item.funktion) return [];
    return [{ relationType: "Mensch fürs Unternehmen", function: item.funktion, isExtern: !!item.isExtern }];
  }
  if (kind === "statusBezug") {
    if (!item.rolle) return [];
    // Beziehungsrollen sind Haushaltszeilen, keine erbrachten Funktionen: Der
    // Beziehungstyp IST die Verwandtschaft. Dieselbe Auflösung wie unten bei
    // `household` — es muss dieselbe sein, sonst schreibt der Statusblock etwas
    // anderes als die Liste weiter unten, obwohl beide dasselbe meinen.
    if (item.imHaushalt !== undefined) {
      const isFree = item.verwandtschaft === RELATIONSHIP_FREE;
      const relationType = isFree
        ? (item.freitext ?? "").trim()
        : (item.verwandtschaft ?? "").trim();
      // Ohne Beziehungstyp KEINE Beziehung — hier verschwände sie sonst still,
      // genau wie eine Haushaltszeile ohne Label.
      if (!relationType) return [];
      const reverseRelationType = isFree
        ? (item.freitextRueck ?? "").trim() || undefined
        : undefined;
      return [{ relationType, reverseRelationType, sameHousehold: item.imHaushalt }];
    }
    return [bezugZuRelation(item.rolle)];
  }
  if (kind === "vertretung") {
    const rollenListe: string[] =
      Array.isArray(item.rollen) && item.rollen.length > 0
        ? item.rollen
        : item.rolle
          ? [item.rolle]
          : [];
    if (!rollenListe.length) return [];
    return rollenListe.map((r) => ({
      relationType: r,
      reverseRelationType: vertretungsGegenrolle(r) ?? undefined,
    }));
  }
  if (kind === "firmaConn") {
    if (!item.role) return [];
    const out: RelBody = { relationType: item.role };
    const sh = (item.share ?? "").trim?.() ?? "";
    if (sh) out.share = sh;
    return [out];
  }
  if (kind === "household") {
    // Konkretes Label direkt (kein Ableiten mehr). Freitext: zwei Richtungen.
    const isFree = item.relationLabel === RELATIONSHIP_FREE;
    const relationType = isFree ? (item.freeRole ?? "").trim() : item.relationLabel;
    if (!relationType) return [];
    const reverseRelationType = isFree ? ((item.freeRoleReverse ?? "").trim() || undefined) : undefined;
    return [{ relationType, reverseRelationType, sameHousehold: !!item.sameHousehold }];
  }
  if (kind === "assistenz") {
    return [{
      relationType: ASSISTENZ_ROLLE,
      reverseRelationType: ASSISTENZ_GEGENROLLE,
      sameHousehold: false,
      ...((item.aufgabe ?? "").trim() ? { function: item.aufgabe.trim() } : {}),
    }];
  }
  return [];
}

/**
 * Woher ein Unterkontakt ableitet (Plan 2026-09-30). Die Regeln stehen im
 * Server (`POST /contact`): `verantwortungVon` → FO/BO/Inhouse des Kontakts
 * (C3–C11), `imHaushaltVon` → dessen Standardanschrift, eine eigene
 * abweichende als weitere Anschrift, Status „Haushaltsmitglied" (A1/A3/D1).
 */
export type AbleitungsBezug = { verantwortungVon?: number; imHaushaltVon?: number };

export function buildSubContactPayload(
  ref: ContactRef,
  ownership: Ownership,
  ids: { person?: number; firma?: number },
  sharedAddress?: SharedAddress,
  statusName?: string,
  bezug: AbleitungsBezug = {},
): Record<string, unknown> {
  // Mit Bezug erbt der Server — dann keine eigene Verantwortung mitschicken,
  // sonst gewönne sie (ausdrückliche FO/BO schlagen die Vererbung).
  const own = bezug.verantwortungVon ? {} : ownershipPayload(ownership);
  const bezugFelder = {
    ...(bezug.verantwortungVon ? { verantwortungVonKontaktId: bezug.verantwortungVon } : {}),
    ...(bezug.imHaushaltVon ? { imHaushaltVon: bezug.imHaushaltVon } : {}),
  };
  // Im Haushalt setzt der Server die Anschrift des Hauptkontakts — hier
  // reist nur die EIGENE mit (A3: sie wird dort zur weiteren Anschrift).
  if (bezug.imHaushaltVon) sharedAddress = undefined;

  // Anschrift: Ein Haushaltsmitglied wohnt DORT, wo der Hauptkontakt wohnt —
  // die gemeinsame Anschrift gewinnt (User 28.09.2026). Bis dahin gewann eine
  // am Eintrag selbst erfasste Anschrift: Wer eine Person zuerst unter
  // „Beziehungen außerhalb des Haushaltes" (dort mit Anschriftfeldern) erfasste
  // und dann in den Haushalt zog, legte sie mit der alten Anschrift an.
  // Sonst (Person außerhalb des Haushalts, Firma) zählt die eigene.
  const eigene =
    ref.street || ref.postalCode || ref.city
      ? {
          street: ref.street,
          postalCode: ref.postalCode,
          city: ref.city,
          ortsteil: ref.cityArea || ref.ortsteil,
          country: ref.country || "Deutschland",
        }
      : undefined;
  const gemeinsam =
    sharedAddress && [sharedAddress.street, sharedAddress.postalCode, sharedAddress.city].some((v) => (v ?? "").trim())
      ? { ...sharedAddress, country: (sharedAddress.country ?? "").trim() || "Deutschland" }
      : undefined;
  const addrSource = gemeinsam ?? eigene;

  const addrParts = addrSource
    ? [addrSource.street, addrSource.postalCode, addrSource.city, addrSource.ortsteil, addrSource.country]
    : [];
  const addr =
    addrParts.some((v) => (v ?? "").trim())
      ? {
          addresses: [
            {
              addressDescription: ref.type === "Firma" ? "Geschäftsanschrift" : "Wohnanschrift",
              street: addrSource!.street ?? "",
              postalCode: addrSource!.postalCode ?? "",
              city: addrSource!.city ?? "",
              ortsteil: addrSource!.ortsteil ?? "",
              country: addrSource!.country ?? "",
              isDefault: true,
            },
          ],
        }
      : {};

  if (ref.type === "Firma") {
    const name = (ref.lastName || "").trim();
    // Firma: Telefon + Mobil + E-Mail (+ Internet) als einzelne Kanäle.
    const comms: CommItem[] = [];
    if (ref.phone?.trim()) comms.push({ type: "phone", value: ref.phone.trim() });
    if (ref.mobile?.trim()) comms.push({ type: "mobile", value: ref.mobile.trim() });
    if (ref.email?.trim()) comms.push({ type: "email", value: ref.email.trim() });
    if (ref.web?.trim()) comms.push({ type: "homepage", value: ref.web.trim() });
    if (ref.fax?.trim()) comms.push({ type: "fax", value: ref.fax.trim() });
    return {
      lastname: name,
      displayName: name,
      ...(statusName ? { statusName } : {}),
      type: ids.firma != null ? { id: ids.firma } : { id: 1, type: "Firma" },
      // Rechtsform und Branche gingen bisher verloren: eingetippt wurden sie,
      // gesendet nicht — die neue Firma landete ohne beides in der Akte.
      ...(ref.legalFormId
        ? { legalForm: { id: ref.legalFormId, legalForm: ref.legalForm ?? "" } }
        : ref.legalForm?.trim()
          ? { legalForm: ref.legalForm.trim() }
          : {}),
      // Konkrete Bezeichnung bei „Sonstige"/„Ausländische Rechtsform" — der
      // Katalogeintrag allein sagt dort nichts aus.
      ...(ref.rechtsform?.trim() ? { rechtsform: ref.rechtsform.trim() } : {}),
      ...(ref.branchengruppeId ? { branchengruppeId: ref.branchengruppeId } : {}),
      ...(ref.brancheFreitext?.trim() ? { brancheFreitext: ref.brancheFreitext.trim() } : {}),
      ...(ref.branche?.trim() ? { jobIndustry: ref.branche.trim() } : {}),
      ...(comms.length ? { communications: comms } : {}),
      // Steuer- und Registerangaben (Visitenkarte, P4) — dieselben Schlüssel
      // wie die Firmen-Vollanlage (buildCompanyContactCreatePayload).
      ...(ref.umsatzsteuernummer?.trim() ? { umsatzsteuernummer: ref.umsatzsteuernummer.trim() } : {}),
      ...(ref.register?.trim() ? { register: ref.register.trim() } : {}),
      ...(ref.handelsregisterNummer?.trim() ? { handelsregisterNummer: ref.handelsregisterNummer.trim() } : {}),
      ...(ref.registergericht?.trim() ? { registergericht: ref.registergericht.trim() } : {}),
      ...addr,
      ...own,
      ...bezugFelder,
    };
  }

  const lastname = (ref.lastName || "").trim();
  const firstName = (ref.firstName || "").trim();
  // Person: Telefon/Mobil/E-Mail je privat/geschäftlich → type + label.
  const comms = buildPersonCommItems(ref);
  if (Array.isArray(ref.weitereKommunikation) && ref.weitereKommunikation.length > 0) {
    for (const extra of ref.weitereKommunikation) {
      if (extra?.value?.trim()) {
        comms.push({
          type: extra.type,
          label: extra.label?.trim() || undefined,
          kind: extra.zuordnung || "geschaeftlich",
          value: extra.value.trim(),
        });
      }
    }
  }
  return {
    lastname,
    displayName: firstName ? `${lastname}, ${firstName}` : lastname,
    ...(statusName ? { statusName } : {}),
    ...(firstName ? { firstName } : {}),
    // Das Geschlecht kommt normalerweise aus der Anrede-Auswahl im Feld mit;
    // ist es leer (Suchtreffer ohne gender, Altbestand), wird es hier aus dem
    // Anrede-Label abgeleitet. Sonst legten Haushalt und „Menschen fürs
    // Unternehmen" Personen ohne Geschlecht an, und die Verwandtschaftslogik
    // fiele auf geschlechtsneutrale Bezeichnungen zurück.
    ...(ref.gender || geschlechtAusAnrede(ref.salutationLabel)
      ? { gender: ref.gender || geschlechtAusAnrede(ref.salutationLabel) }
      : {}),
    ...(ref.salutationId
      ? { salutation: { id: ref.salutationId } }
      : ref.salutationLabel
        ? { salutationName: ref.salutationLabel }
        : {}),
    ...(ref.titleId
      ? { title: { id: ref.titleId } }
      : ref.titleLabel
        ? { titleName: ref.titleLabel }
        : {}),
    ...(ref.birthDate ? { birthDate: ref.birthDate } : {}),
    ...(ids.person != null ? { type: { id: ids.person } } : {}),
    ...(comms.length ? { communications: comms } : {}),
    ...addr,
    ...own,
    ...bezugFelder,
  };
}

function extractId(created: any): number | undefined {
  const id = created?.id ?? created?.contact?.id;
  return typeof id === "number" ? id : undefined;
}

/**
 * Legt einen frei eingetippten Kontakt SOFORT an und liefert seine id — für die
 * Fälle, in denen der Aufrufer die id schon vor dem eigentlichen Speichern
 * braucht (z. B. „Verbindung zu Firma": der attach-menschen-Endpoint verlangt
 * eine bestehende companyId, die neue Firma muss also vorher existieren).
 *
 * Nutzt bewusst dasselbe `buildSubContactPayload` wie die Sammel-Anlage, damit
 * Rechtsform, Branche und Kommunikationskanäle auf beiden Wegen identisch
 * gesendet werden — genau das war früher auseinandergelaufen.
 */
export async function createContactFromRef(
  ref: ContactRef,
  ownership: Ownership = {},
  statusName?: string,
  /**
   * C2/C5 (Plan 2026-09-30): Kontakt, aus dem heraus angelegt wird — der
   * Server übernimmt dessen FO/BO. Gewinnt gegen `ownership`.
   */
  verantwortungVon?: number,
  partnerProfile?: Omit<PartnerProfile, "contactId">,
): Promise<number | undefined> {
  if (!refHasName(ref)) return undefined;
  const ids = await resolveTypeIds();
  const bezug: AbleitungsBezug =
    Number.isInteger(verantwortungVon) && (verantwortungVon as number) > 0 ? { verantwortungVon } : {};
  const res = await fetch(apiUrl("contact"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...buildSubContactPayload(ref, ownership, ids, undefined, statusName, bezug), ...(partnerProfile ? { partnerProfile } : {}) }),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => null);
    throw new Error(typeof error?.message === "string" ? error.message : `Kontakt anlegen fehlgeschlagen (${res.status})`);
  }
  const id = extractId(await res.json().catch(() => null));
  if (id != null) {
    await speichereWeitereBranchen(id, ref);
    await logoNachreichen(id, ref);
  }
  return id;
}

/**
 * Rollen am gespiegelten NEUEN Arbeitgeber nachreichen, sobald die Firma über
 * den Arbeitgeber-Weg angelegt ist (`persistCollectedRelations` überspringt
 * den Eintrag, damit sie nicht doppelt entsteht). Ohne Rolle gibt es nichts
 * zu tun — die Zeile „Mensch im Unternehmen" legt `PUT arbeitgeber` an.
 * Liefert Fehlertexte.
 */
export async function arbeitgeberEintragNachreichen(
  mainId: number,
  firmaId: number,
  personFirma: PersonFirmaItem[],
): Promise<string[]> {
  const fehler: string[] = [];
  const eintrag = personFirma.find((it) => it?.autoAusArbeitgeber && it.ref && !it.ref.contactId);
  if (!eintrag) return fehler;
  const { src, dest } = relationDirection("personFirma", mainId, firmaId, eintrag);
  for (const body of relationBodies("personFirma", eintrag)) {
    try {
      const res = await fetch(apiUrl("contact-relation"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contactSrcId: src, contactDestId: dest, ...body }),
      });
      if (!res.ok) fehler.push(`„${body.relationType}" zum Arbeitgeber konnte nicht angelegt werden (${res.status}).`);
    } catch {
      fehler.push(`„${body.relationType}" zum Arbeitgeber konnte nicht angelegt werden (keine Verbindung).`);
    }
  }
  return fehler;
}

/**
 * Das beim Inline-Anlegen bestätigte Logo („Ist das das Logo der Firma?" → Ja)
 * an der frisch angelegten Firma speichern. Ohne `ersetzen`: Die Firma ist
 * neu, ein vorhandenes Bild gibt es nicht. Scheitert der Schritt, bleibt die
 * Firma bestehen — ein fehlendes Logo ist ein Nachtrag, kein Fehlschlag.
 */
async function logoNachreichen(contactId: number, ref: ContactRef): Promise<void> {
  const bild = gemerktesLogo(ref.logoVorschlag);
  if (!bild) return;
  try {
    await logoUebernehmen(contactId, bild, false);
    logoVergessen(ref.logoVorschlag);
  } catch (e) {
    console.error("Logo konnte nicht übernommen werden", e);
  }
}

/**
 * Die Branchen einer frisch angelegten Firma nachreichen.
 *
 * Die Zuordnungstabelle führt die VOLLSTÄNDIGE Liste (erste Zeile =
 * Hauptbranche) — die Hauptbranche geht deshalb hier mit, obwohl sie schon im
 * Kontakt-Payload steht (`branchengruppeId`). Bei nur einer Branche gibt es
 * nichts nachzureichen: Der Kontakt-Payload hat sie bereits.
 *
 * Scheitert der Schritt, bleibt die Firma bestehen: Eine fehlende Zweitbranche
 * ist ein Nachtrag, kein Grund, eine gerade angelegte Firma als Fehlschlag zu
 * melden.
 */
async function speichereWeitereBranchen(contactId: number, ref: ContactRef): Promise<void> {
  const zeilen = [
    ...(Number(ref.branchengruppeId) > 0
      ? [
          {
            branchengruppeId: Number(ref.branchengruppeId),
            freitext: (ref.brancheFreitext ?? "").trim() || null,
          },
        ]
      : []),
    // Der Freitext der WEITEREN Zeilen wandert mit: Seit die Auffangbranche
    // ihre Auffanggruppe selbst anhakt, kann er auch an einer Zweitbranche
    // hängen — verwürfe man ihn hier, stünde in der Akte am Ende nur
    // „Sonstiges", obwohl der Anwender ihn im Popup ausgefüllt hat.
    ...weitereBranchenNormalisieren(ref.weitereBranchengruppen).map((w) => ({
      branchengruppeId: w.branchengruppeId,
      freitext: (w.freitext ?? "").trim() || null,
    })),
  ];
  if (zeilen.length <= 1) return;
  try {
    await branchengruppenSpeichern(contactId, zeilen);
  } catch (e) {
    console.error("Weitere Branchen konnten nicht gespeichert werden", e);
  }
}

/**
 * Richtung + kanonischer relationType einer Beziehung — EINE Quelle für Anlage
 * (POST) und Abgleich (Umwandlung), damit beide nicht auseinanderlaufen:
 *   personFirma → Firma = src (Person = dest)
 *   firmaConn   → Rolle→KANONISCH ('Mutterfirma' flippt die Richtung und wird
 *                 als 'Tochterfirma' gespeichert; die Backend-Schwester-Kaskade
 *                 läuft dann von selbst)
 *   statusBezug → je Rolle (`data/statusBezugMapping.ts`); nur die
 *                 Haushaltsrollen bleiben bei „Hauptkontakt = src", wie die
 *                 Haushaltsliste auch
 *   sonst       → Hauptkontakt = src
 */
export function relationDirection(
  kind: RelKind,
  mainId: number,
  otherId: number,
  item: any,
): { src: number; dest: number; typeOverride?: string } {
  if (kind === "firmaConn") {
    const canon = canonicalFirmaConn((item as FirmaConnItem).role, mainId, otherId);
    return { src: canon.contactSrcId, dest: canon.contactDestId, typeOverride: canon.relationType };
  }
  // `personFirma` und `vertretung` teilen die Richtung: Der ANDERE ist `src`,
  // dieser Kontakt `dest` — „was dest für src ist" ergibt „Betreuer von B".
  if (kind === "personFirma" || kind === "vertretung") return { src: otherId, dest: mainId };
  // Die Statusrolle sagt, was DIESER Kontakt für den anderen ist — also steht
  // in aller Regel der andere in `src`. Ausgenommen die Haushaltsrollen: Ihr
  // Beziehungstyp ist die Verwandtschaft („was die Person für diesen Kontakt
  // ist") und läuft wie jede Haushaltszeile vom Hauptkontakt aus.
  if (kind === "statusBezug" && (item as StatusBezugItem).imHaushalt === undefined) {
    const kante = bezugKante((item as StatusBezugItem).rolle, mainId, otherId);
    return { src: kante.contactSrcId, dest: kante.contactDestId };
  }
  return { src: mainId, dest: otherId };
}

/**
 * Symmetrische Typen: „A ist Schwester von B" gilt in beide Richtungen. Für sie
 * ist ein Richtungsunterschied KEIN Änderungswunsch — sonst legte jedes
 * Speichern die Zeile sinnlos neu an. Joint Venture gehört fachlich dazu (zwei
 * Partner halten ein gemeinsames Unternehmen).
 *
 * „Beteiligung" bleibt hier bewusst draussen — sie war schon vorher nicht in
 * dieser Menge und ist seit 09/2026 endgültig gerichtet (src = Gesellschaft).
 */
const SYMMETRIC_RELATION_TYPES: ReadonlySet<string> = new Set([SCHWESTER, JOINT_VENTURE]);

/**
 * Alle Backend-Zeilen, für die ein Eintrag steht. `relationIds` ist die
 * vollständige Liste (mehrere Rollen hinter EINEM Kärtchen), `relationId` der
 * Einzelfall.
 */
function relationIdsOf(item: any): number[] {
  const list = Array.isArray(item?.relationIds)
    ? (item.relationIds as unknown[]).filter((n): n is number => typeof n === "number")
    : [];
  if (list.length) return list;
  return typeof item?.relationId === "number" ? [item.relationId] : [];
}

/**
 * Spiegelzeilen eines Firmen-Eintrags, die er nach dem Speichern WEITERHIN
 * abdeckt: die, die dieselbe Beteiligung meinen wie die Zeile, die der Eintrag
 * jetzt beschreibt (data/anteilsbeziehung.ts).
 *
 * Wird aus „Tochterfirma" etwa „Schwester", meint der Eintrag keine
 * Beteiligung mehr — die Gesellschafter-Zeile daneben fällt dann mit weg. Wird
 * nur die Quote geändert oder aus „Tochterfirma" eine „Beteiligung" derselben
 * Richtung, bleibt sie stehen und bekommt die neue Quote.
 */
export function behalteneSpiegel(kind: RelKind, mainId: number, item: any): number[] {
  if (kind !== "firmaConn") return [];
  const spiegel = Array.isArray(item?.spiegel) ? (item.spiegel as any[]) : [];
  if (!spiegel.length) return [];
  const otherId = item?.ref?.contactId;
  if (typeof otherId !== "number" || !item?.role) return [];
  // Ohne `mainId` (Bestätigungsdialog) aus der gespeicherten Leitzeile ableiten:
  // der Kontakt, der nicht der Gegenkontakt ist.
  const selbst = mainId || (item.srcId === otherId ? item.destId : item.srcId);
  if (typeof selbst !== "number") return [];
  const dir = relationDirection(kind, selbst, otherId, item);
  const neu = anteilsFakt(dir.typeOverride ?? item.role, dir.src, dir.dest);
  return spiegel
    .filter((sp) => typeof sp?.id === "number" && gleicheBeteiligung(anteilsFakt(sp.typ, sp.srcId, sp.destId), neu))
    .map((sp) => sp.id as number);
}

/** Alle Spiegel-Ids eines Eintrags (ohne Prüfung). */
function spiegelIdsOf(item: any): number[] {
  return Array.isArray(item?.spiegel)
    ? (item.spiegel as any[]).map((sp) => sp?.id).filter((n): n is number => typeof n === "number")
    : [];
}

/**
 * Zeilen, die dieser Eintrag weiterhin abdeckt — eine je Rolle, in Reihenfolge.
 *
 * Grundlage für BEIDE Richtungen: der Abgleich paart hierüber Zeilen mit Rollen,
 * und `relationsRemovedBy` leitet daraus ab, was ersatzlos entfällt. Eine zweite,
 * eigenständige Ableitung für den Bestätigungsdialog würde früher oder später
 * etwas anderes ankündigen, als tatsächlich passiert.
 */
function coveredIds(kind: RelKind, item: any): number[] {
  const ids = relationIdsOf(item);
  if (ids.length === 0) return [];
  // Gegenkontakt aus dem Eintrag entfernt → die Zeilen entfallen.
  if (typeof item?.ref?.contactId !== "number") return [];
  return ids.slice(0, relationBodies(kind, item).length);
}

/** Alle Einträge einer Sammlung mit ihrer Art — Reihenfolge wie im Abgleich. */
function flattenCollections(collections: RelationCollections): Array<{ kind: RelKind; item: any }> {
  return [
    ...(collections.firmaConn ?? []).map((item) => ({ kind: "firmaConn" as RelKind, item })),
    // Die Liste entscheidet über `sameHousehold` (Verschieben per Ziehen/Knopf).
    ...(collections.household ?? []).map((item) => ({ kind: "household" as RelKind, item: { ...item, sameHousehold: true } })),
    ...(collections.weitere ?? []).map((item) => ({ kind: "household" as RelKind, item: { ...item, sameHousehold: false } })),
    ...(collections.assistenz ?? []).map((item) => ({ kind: "assistenz" as RelKind, item })),
    ...(collections.personFirma ?? []).map((item) => ({ kind: "personFirma" as RelKind, item })),
    // Vertretungen: Bis 27.09.2026 kannte der Abgleich sie nicht — eine neu
    // erfasste Vertretung ging beim Umwandeln still verloren, eine bestehende
    // hätte der Lösch-Schritt entfernt.
    ...(collections.vertretung ?? []).map((item) => ({ kind: "vertretung" as RelKind, item })),
  ];
}

/**
 * Fingerabdruck dessen, was ein Eintrag in der Datenbank bedeutet: Richtung,
 * Gegenkontakt, Zeilen und die daraus entstehenden Rümpfe.
 *
 * Der Bearbeiten-Modus merkt ihn sich beim Laden (`bestandSignatur`). Stimmt er
 * beim Speichern noch, fasst der Abgleich die Zeilen NICHT an. Sonst schriebe
 * jedes Speichern jede Beziehung neu — und der PATCH leert dabei Angaben, die
 * die Maske gar nicht führt (etwa eine gespeicherte Gegenrolle).
 */
export function relationSignatur(kind: RelKind, mainId: number, item: any): string {
  const otherId = item?.ref?.contactId;
  if (typeof otherId !== "number") return "";
  const dir = relationDirection(kind, mainId, otherId, item);
  return JSON.stringify({
    kind,
    otherId,
    dir,
    srcId: item?.srcId ?? null,
    destId: item?.destId ?? null,
    ids: relationIdsOf(item),
    bodies: relationBodies(kind, item),
  });
}

/**
 * Welche bestehenden Beziehungen würde ein Abgleich ERSATZLOS entfernen?
 *
 * Für den Bestätigungsdialog beim Umwandeln: die Maske ist vorausgefüllt, ein
 * versehentlich entfernter Eintrag löscht eine echte Beziehung. Gezählt wird,
 * was kein Eintrag mehr abdeckt — also entfernte Einträge, geleerte
 * Gegenkontakte UND Einträge ohne vergebene Rolle (beim Umwandeln kommen
 * rollenlose Beziehungen bewusst leer zurück; bleiben sie leer, verschwinden sie).
 *
 * Ein Richtungswechsel zählt NICHT als Verlust — die Zeile wird ersetzt.
 */
export function relationsRemovedBy(
  collections: RelationCollections,
  previousRelationIds: number[],
): number[] {
  const covered = new Set<number>();
  for (const { kind, item } of flattenCollections(collections)) {
    for (const id of coveredIds(kind, item)) covered.add(id);
    // Spiegelzeilen (dieselbe Beteiligung in anderer Speicherform) bleiben,
    // solange der Eintrag noch dieselbe Beteiligung meint.
    if (coveredIds(kind, item).length) for (const id of behalteneSpiegel(kind, 0, item)) covered.add(id);
  }
  return previousRelationIds.filter((id) => typeof id === "number" && !covered.has(id));
}

/**
 * Dieselbe Liste, aber benannt — für den Bestätigungsdialog. `labels` kommt aus
 * dem Prefill (`rel_previousLabels`), weil die Beziehung nach dem Entfernen aus
 * der Maske nicht mehr abfragbar ist.
 */
export function describeRemovedRelations(
  collections: RelationCollections,
  previousRelationIds: number[],
  labels: Record<string, string>,
): string[] {
  return relationsRemovedBy(collections, previousRelationIds).map(
    (id) => labels?.[String(id)] ?? `Beziehung #${id}`,
  );
}

/** Name eines Gegenkontakts für Fehlermeldungen. */
const refAnzeigeName = (ref: ContactRef): string =>
  [ref.lastName, ref.firstName].map((x) => String(x ?? "").trim()).filter(Boolean).join(", ") ||
  String((ref as { displayName?: string }).displayName ?? "").trim() ||
  "Unbenannter Kontakt";

/** Ergebnis eines Abgleichs — die Oberfläche muss Fehlschläge zeigen können. */
export interface RelationSyncReport {
  patched: number;
  created: number;
  deleted: number;
  /**
   * Nicht durchgegangene Schreibvorgänge im Klartext. Wichtig, weil ein 403/404
   * KEINE Exception ist: ohne diese Prüfung meldet das Speichern Erfolg, obwohl
   * nichts geschrieben wurde (dieselbe Falle wie beim Status-Patch, #1065).
   */
  failures: string[];
}

/**
 * Persistiert alle gesammelten Verknüpfungen NACH dem Anlegen des Hauptkontakts.
 * @param mainId  id des gerade angelegten Haupt-Kontakts (Firma bzw. Person)
 */
export async function persistCollectedRelations(
  mainId: number,
  collections: RelationCollections,
  ownership: Ownership,
  opts?: {
    householdAddress?: SharedAddress;
    householdGroupName?: string;
    /**
     * Sammelt Fehlschläge im Klartext. Ohne sie scheiterten neue Einträge
     * still (nur console.warn) — im Bearbeiten-Modus der Akte meldete das
     * Schloss dann Erfolg, obwohl die neue Beziehung fehlte (Befund 28.09.2026).
     */
    failures?: string[];
    /**
     * A2 (Plan 2026-09-30, Entscheidung 30.09.2026): Ein BESTEHENDER Kontakt,
     * der in den Haushalt kommt, übernimmt die Standardanschrift des
     * Hauptkontakts automatisch — ohne Rückfrage. Seine bisherige
     * Standardanschrift bleibt als weitere Anschrift (der Server legt keine
     * Dublette an). `false` schaltet die Übernahme ab.
     */
    anschriftUebernehmen?: boolean;
  },
): Promise<void> {
  const ids = await resolveTypeIds();
  const fehler = (text: string) => opts?.failures?.push(text);
  // Haushaltsmitglieder (neu + bestehend) → gemeinsame Haushalts-Gruppe am Ende.
  const householdMemberIds: number[] = [];

  const groups: Array<{ kind: RelKind; items: Array<GfgItem | VerantItem | FirmaConnItem | PersonFirmaItem | HouseholdItem | AssistantItem | StatusBezugItem | VertretungItem> }> = [
    { kind: "gfg", items: collections.gfg ?? [] },
    { kind: "verant", items: collections.verant ?? [] },
    { kind: "firmaConn", items: collections.firmaConn ?? [] },
    // Haushalt (sameHousehold=true) + weitere Beziehungen (sameHousehold=false) —
    // gleiche Ableitungslogik. Maßgeblich ist die LISTE, in der ein Eintrag
    // steht, nicht ein mitgeschlepptes Flag: Ein Eintrag der Haushaltsliste
    // ist ein Haushaltsmitglied (Anschrift, Haushalts-Gruppe, sameHousehold).
    {
      kind: "household",
      items: [
        ...(collections.household ?? []).map((it) => ({ ...it, sameHousehold: true })),
        ...(collections.weitere ?? []).map((it) => ({ ...it, sameHousehold: false })),
      ],
    },
    { kind: "assistenz", items: collections.assistenz ?? [] },
    { kind: "personFirma", items: collections.personFirma ?? [] },
    // Zuletzt die Statusrollen: Sie können dieselbe Firma treffen, die schon
    // über eine andere Kategorie verbunden wurde — dann steht die fachlich
    // genauere Zeile zuerst.
    { kind: "statusBezug", items: collections.statusBezug ?? [] },
    { kind: "vertretung", items: collections.vertretung ?? [] },
  ];

  for (const { kind, items } of groups) {
    for (const item of items) {
      try {
        const ref = item.ref;
        if (!ref) continue;
        // Gespiegelter NEUER Arbeitgeber (utils/arbeitgeberVerbindung.ts): Die
        // Firma legt der Arbeitgeber-Weg an (`createContactFromRef` +
        // `PUT arbeitgeber`), hier gäbe es sie sonst ein zweites Mal. Seine
        // Rollen reicht `arbeitgeberEintragNachreichen` nach.
        if (kind === "personFirma" && (item as PersonFirmaItem).autoAusArbeitgeber && !ref.contactId) continue;

        // Echtes Haushaltsmitglied — erbt Ownership + Wohnanschrift (nur wenn
        // neu angelegt) und geht in die Haushalts-Gruppe.
        //
        // Zwei Wege führen hierher, und beide müssen gelten: die Haushalts-
        // Sektion und die Statusrolle „Im selben Haushalt". Fehlte der zweite,
        // bekäme dieselbe Person je nach Eingabeweg eine andere Anschrift und
        // fiele aus der Haushalts-Gruppe — der Unterschied wäre später nicht
        // mehr erklärbar.
        const isHousehold =
          (kind === "household" && (item as HouseholdItem).sameHousehold === true) ||
          (kind === "statusBezug" && (item as StatusBezugItem).imHaushalt === true);

        // (1) Gegenkontakt-id beschaffen — frei eingetippte JETZT erst anlegen.
        let otherId = ref.contactId;
        if (!otherId) {
          if (!refHasName(ref)) continue; // unvollständig → still überspringen
          // Plan 2026-09-30: Verantwortung (C3/C4/C7) und Haushaltsanschrift
          // (A1/A3) leitet der Server aus dem Hauptkontakt ab — eine Regel an
          // einer Stelle statt je Aufrufer ein zusammengebautes Objekt.
          const created = await fetch(apiUrl("contact"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(
              buildSubContactPayload(
                ref,
                ownership,
                ids,
                undefined,
                isHousehold ? "Haushaltsmitglied" : undefined,
                { verantwortungVon: mainId, ...(isHousehold ? { imHaushaltVon: mainId } : {}) },
              ),
            ),
          }).then((r) => (r.ok ? r.json() : null));
          otherId = extractId(created);
          if (!otherId) {
            // Anlage fehlgeschlagen → Beziehung überspringen, aber melden.
            fehler(`„${refAnzeigeName(ref)}" konnte nicht angelegt werden.`);
            continue;
          }
          // Zusätzliche Spezialisierungen einer neu eingetippten Firma — sie
          // brauchen die id und laufen deshalb erst hier.
          await speichereWeitereBranchen(otherId, ref);
          await logoNachreichen(otherId, ref);
          // T-283: Hat der Hauptkontakt keine Anschrift, bringt das neue
          // Mitglied aber eine mit, übernimmt der HAUPTKONTAKT sie. Mit
          // Anschrift des Hauptkontakts hat der Server das Mitglied schon beim
          // Anlegen versorgt (A1) — dann braucht es keinen weiteren Aufruf.
          if (
            isHousehold &&
            !hatHaushaltsanschrift(opts?.householdAddress) &&
            hatHaushaltsanschrift(ref) &&
            opts?.anschriftUebernehmen !== false
          ) {
            const ok = await haushaltsanschriftAbgleichen(otherId, mainId);
            if (!ok) fehler(`Die Anschrift von „${refAnzeigeName(ref)}" konnte nicht übernommen werden.`);
          }
        } else if (isHousehold && opts?.anschriftUebernehmen !== false) {
          // A2: Bestandskontakt in den Haushalt gezogen — er übernimmt die
          // Anschrift des Haushalts automatisch (Entscheidung 30.09.2026).
          // T-283: Hat der Hauptkontakt keine, übernimmt umgekehrt ER die
          // Standardanschrift des Mitglieds; die Richtung prüft der Server.
          const ok = await haushaltsanschriftAbgleichen(otherId, mainId);
          if (!ok) fehler(`Die Anschrift konnte für „${refAnzeigeName(ref)}" nicht übernommen werden.`);
        }

        // Haushaltsmitglied (neu wie bestehend) für die Haushalts-Gruppe vormerken.
        if (isHousehold && otherId && !householdMemberIds.includes(otherId)) {
          householdMemberIds.push(otherId);
        }

        // (2) Richtung + relationType bestimmen (gemeinsam mit dem Abgleich).
        const { src, dest, typeOverride: relationTypeOverride } = relationDirection(
          kind,
          mainId,
          otherId,
          item,
        );

        // Pro Eintrag eine ODER mehrere Beziehungen (z. B. mehrere
        // Verantwortlichkeit-Rollen für dieselbe Firma). Der kanonische
        // relationType (firmaConn) überschreibt den aus relationBodies; share bleibt.
        const bodies = relationBodies(kind, item);
        for (const body of bodies) {
          const relationType = relationTypeOverride ?? body.relationType;
          const res = await fetch(apiUrl("contact-relation"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              contactSrcId: src,
              contactDestId: dest,
              ...body,
              ...(relationTypeOverride ? { relationType: relationTypeOverride } : {}),
            }),
          });
          if (res && !res.ok) {
            fehler(`„${relationType}" zu „${refAnzeigeName(ref)}" konnte nicht angelegt werden (${res.status}).`);
          }
        }
      } catch (err) {
        // Ein Eintrag darf weder die Schleife noch die bereits angelegte
        // Hauptanlage abbrechen.
        console.warn("persistCollectedRelations: Eintrag fehlgeschlagen", { kind, err });
        fehler(`Eine neue Beziehung konnte nicht angelegt werden (${kind}).`);
      }
    }
  }

  // (3) Haushalt: bei ≥1 Haushaltsmitglied Hauptkontakt + Mitglieder einer
  //     ContactGroup(type='Haushalt') zuweisen. Lebt einer von ihnen (zuerst der
  //     Hauptkontakt) schon in einem Haushalt, ERGÄNZT der Server diesen, statt
  //     einen weiteren anzulegen — wichtig für die Akte, die hier bei jedem
  //     Speichern mit neuen Mitgliedern vorbeikommt (Befund 28.09.2026).
  //     Fehler brechen die Anlage nicht ab.
  if (householdMemberIds.length && (opts?.householdGroupName ?? "").trim()) {
    try {
      await fetch(apiUrl("contactgroup/household"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: opts!.householdGroupName!.trim(),
          memberContactIds: [mainId, ...householdMemberIds],
        }),
      });
    } catch (err) {
      console.warn("persistCollectedRelations: Haushalts-Gruppe fehlgeschlagen", err);
    }
  }
}

/** Hat die Anschrift (des Haushalts oder eines Eintrags) Straße, PLZ oder Ort? */
function hatHaushaltsanschrift(a?: { street?: string; postalCode?: string; city?: string }): boolean {
  return !!a && [a.street, a.postalCode, a.city].some((v) => (v ?? "").trim());
}

async function haushaltsanschriftSenden(
  kontaktId: number,
  body: { vonKontaktId: number; beideRichtungen?: true },
): Promise<boolean> {
  try {
    const res = await fetch(apiUrl(`contact/${kontaktId}/haushaltsanschrift`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * A2/A4 (Plan 2026-09-30): Kontakt `zielId` übernimmt die Standardanschrift
 * von `vonId`; die bisherige bleibt als weitere Anschrift. `true` bei Erfolg.
 * Nur in diese eine Richtung — der Umzug (A4) braucht genau das.
 */
export async function haushaltsanschriftUebernehmen(zielId: number, vonId: number): Promise<boolean> {
  return haushaltsanschriftSenden(zielId, { vonKontaktId: vonId });
}

/**
 * T-283 (Dirk 10.10.2026): `mitgliedId` kommt in den Haushalt von `hauptId`,
 * und die Anschrift wandert dorthin, wo sie fehlt: Hat der Hauptkontakt eine,
 * übernimmt das Mitglied sie (A2); hat er keine, übernimmt der Hauptkontakt
 * die Standardanschrift des Mitglieds. Die Richtung prüft der Server
 * (`beideRichtungen`), weil nur er beide Akten kennt. `true` bei Erfolg.
 */
export async function haushaltsanschriftAbgleichen(mitgliedId: number, hauptId: number): Promise<boolean> {
  return haushaltsanschriftSenden(mitgliedId, { vonKontaktId: hauptId, beideRichtungen: true });
}

/**
 * Weist den (bereits angelegten) Kontakt einer bestehenden Kundenverbindung zu —
 * und seine Haushaltsmitglieder mit (C12, Plan 2026-09-30): Wer im Haushalt
 * lebt, gehört zur selben Kundenverbindung. In der Anlage entsteht der
 * Haushalt VOR der Zuordnung, deshalb wird hier nachgezogen; kommt ein
 * Mitglied später dazu, erledigt das der Server beim Speichern der Beziehung.
 */
export async function assignKundenverbindung(mainId: number, contactGroupId: number): Promise<void> {
  try {
    await fetch(apiUrl(`contact/${mainId}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contactGroupId }),
    });
  } catch (err) {
    console.warn("assignKundenverbindung fehlgeschlagen", err);
    return;
  }
  try {
    const { data } = await apiFetch<{ mitglieder?: Array<{ id?: number }> }>(`contact-relation/haushalt/${mainId}`);
    const mitglieder = (data?.mitglieder ?? [])
      .map((m) => m?.id)
      .filter((id): id is number => typeof id === "number" && id !== mainId);
    for (const id of mitglieder) {
      await fetch(apiUrl(`contactgroup/${contactGroupId}/contacts`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contactId: id }),
      });
    }
  } catch (err) {
    console.warn("Kundenverbindung für den Haushalt fehlgeschlagen", err);
  }
}

/**
 * Beziehungen eines BESTEHENDEN Kontakts abgleichen — für die Umwandlung
 * (Firma ↔ Person), wo die Sektionen vorausgefüllt sind.
 *
 * `persistCollectedRelations` kennt nur POST; hier angewandt entstünde für jede
 * vorausgefüllte Beziehung eine zweite Zeile. Deshalb vier Fälle:
 *
 *   Zeile bleibt, Rolle geändert   → PATCH contact-relation/:id
 *   Rolle ohne Zeile (neu)         → POST
 *   Zeile ohne Rolle (entfernt)    → DELETE contact-relation/:id
 *   Richtung dreht sich            → POST neu, DANN alte Zeile löschen
 *
 * Der PATCH auf die Zeilen-id ist bewusst gewählt: Der Convert-Endpoint schreibt
 * die Rollen serverseitig um, BEVOR dieser Abgleich läuft. Ein POST mit dem
 * alten Typ würde die Backend-Deduplizierung verfehlen (sie matcht auf
 * relationTypeId und richtungs-abhängig) und eine zweite Zeile anlegen — ein
 * PATCH auf die Zeile trifft immer. Die Richtung lässt der Endpoint unangetastet
 * (nur relationTypeId/function/share), der Prefill-Schnappschuss bleibt also
 * gültig.
 *
 * `previousRelationIds` sind die beim Laden vorhandenen Beziehungs-Ids — aber
 * NUR die, für die dieser Abgleich zuständig ist. Zeilen, die ein anderer
 * Endpoint verwaltet (`attach-menschen` ersetzt die Rollen einer Person
 * transaktional), gehören nicht hierher, sonst löscht Schritt (3), was der
 * andere gerade geschrieben hat.
 */
export async function syncCollectedRelations(
  mainId: number,
  collections: RelationCollections,
  previousRelationIds: number[],
  ownership: Ownership,
  opts?: { householdAddress?: SharedAddress; householdGroupName?: string },
): Promise<RelationSyncReport> {
  const report: RelationSyncReport = { patched: 0, created: 0, deleted: 0, failures: [] };

  const all = flattenCollections(collections);

  const keptIds = new Set<number>();

  const post = async (src: number, dest: number, body: RelBody): Promise<boolean> => {
    try {
      const res = await fetch(apiUrl("contact-relation"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contactSrcId: src, contactDestId: dest, ...body }),
      });
      if (!res.ok) {
        report.failures.push(`„${body.relationType}" konnte nicht angelegt werden (${res.status}).`);
        return false;
      }
      report.created++;
      return true;
    } catch {
      report.failures.push(`„${body.relationType}" konnte nicht angelegt werden (keine Verbindung).`);
      return false;
    }
  };

  // (1) Bestehende Zeilen abgleichen.
  for (const { kind, item } of all) {
    const ids = relationIdsOf(item);
    if (ids.length === 0) continue; // ganz neu → Schritt (2)

    const otherId = item?.ref?.contactId;
    // Gegenkontakt aus dem Eintrag entfernt → die Zeilen entfallen (Schritt 3).
    if (typeof otherId !== "number") continue;

    // Unverändert seit dem Laden (Bearbeiten-Modus) → nichts schreiben.
    if (
      typeof item?.bestandSignatur === "string" &&
      item.bestandSignatur !== "" &&
      item.bestandSignatur === relationSignatur(kind, mainId, item)
    ) {
      ids.forEach((id) => keptIds.add(id));
      spiegelIdsOf(item).forEach((id) => keptIds.add(id));
      continue;
    }

    // Spiegelzeilen derselben Beteiligung: behalten und die Quote mitziehen —
    // sonst stünde nach dem Speichern die alte Quote an der Gegenzeile, und
    // Akte wie Organigramm meldeten einen Widerspruch.
    for (const sid of behalteneSpiegel(kind, mainId, item)) {
      keptIds.add(sid);
      try {
        const res = await fetch(apiUrl(`contact-relation/${sid}`), {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ share: String(item?.share ?? "").trim() }),
        });
        if (!res.ok) report.failures.push(`Eine Anteilsangabe konnte nicht angeglichen werden (${res.status}).`);
        else report.patched++;
      } catch {
        report.failures.push("Eine Anteilsangabe konnte nicht angeglichen werden (keine Verbindung).");
      }
    }

    // Nur zwischen Haushalt und weiteren Beziehungen verschoben (User
    // 28.09.2026)? Dann genau dieses eine Feld patchen. Der volle PATCH unten
    // leerte sonst die gespeicherte Gegenrolle, und eine EINGEHENDE Zeile
    // (Gegenüber = src) sähe wie ein Richtungswechsel aus und würde gelöscht
    // und neu angelegt.
    if (
      kind === "household" &&
      typeof item?.bestandSignatur === "string" &&
      item.bestandSignatur !== "" &&
      item.bestandSignatur === relationSignatur(kind, mainId, { ...item, sameHousehold: !item.sameHousehold })
    ) {
      for (const id of ids) {
        keptIds.add(id);
        try {
          const res = await fetch(apiUrl(`contact-relation/${id}`), {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sameHousehold: !!item.sameHousehold }),
          });
          if (!res.ok) report.failures.push(`Eine Beziehung konnte nicht verschoben werden (${res.status}).`);
          else report.patched++;
        } catch {
          report.failures.push("Eine Beziehung konnte nicht verschoben werden (keine Verbindung).");
        }
      }
      continue;
    }

    const dir = relationDirection(kind, mainId, otherId, item);
    const bodies = relationBodies(kind, item).map((b) =>
      dir.typeOverride ? { ...b, relationType: dir.typeOverride } : b,
    );

    // Ein Richtungswechsel (Tochterfirma ↔ Mutterfirma) lässt sich per PATCH
    // nicht ausdrücken: die DTO kennt contactSrcId/contactDestId nicht und die
    // globale ValidationPipe läuft mit forbidNonWhitelisted → 400. Solche Zeilen
    // werden ersetzt — erst die neue Richtung anlegen, dann die alte fallen
    // lassen. Nie umgekehrt: ein fehlgeschlagener POST darf die bestehende Zeile
    // nicht kosten.
    const symmetric = SYMMETRIC_RELATION_TYPES.has(dir.typeOverride ?? "");
    const flipped =
      !symmetric &&
      typeof item?.srcId === "number" &&
      typeof item?.destId === "number" &&
      (item.srcId !== dir.src || item.destId !== dir.dest);

    if (flipped) {
      if (bodies.length === 0) continue; // Rolle geleert → Schritt (3) löscht
      let allOk = true;
      for (const body of bodies) {
        if (!(await post(dir.src, dir.dest, body))) allOk = false;
      }
      // Nur wenn die neue Richtung wirklich steht, dürfen die alten Zeilen gehen.
      if (!allOk) ids.forEach((id) => keptIds.add(id));
      continue;
    }

    // Zeilen und Rollen index-weise paaren. Ein Eintrag kann mehrere Rollen
    // bündeln (Geschäftsführer UND Gesellschafter): gleich viele → PATCH, mehr
    // Rollen als Zeilen → die zusätzlichen anlegen, mehr Zeilen als Rollen → die
    // überzähligen fallen in Schritt (3).
    for (let i = 0; i < bodies.length; i++) {
      const body = bodies[i];
      const id = ids[i];
      if (id == null) {
        await post(dir.src, dir.dest, body);
        continue;
      }
      keptIds.add(id);
      try {
        const res = await fetch(apiUrl(`contact-relation/${id}`), {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...body,
            function: body.function ?? "",
            share: body.share ?? "",
            reverseRelationType: body.reverseRelationType ?? "",
            isExtern: !!body.isExtern,
            sameHousehold: !!body.sameHousehold,
            // Nur die Verbindung zu Unternehmen kennt „ausgeschieden" — dort
            // immer mitschicken, damit auch das Zurücknehmen ankommt.
            ...(kind === "personFirma"
              ? {
                  ausgeschieden: !!body.ausgeschieden,
                  beginn: body.beginn ?? null,
                  ende: body.ende ?? null,
                }
              : {}),
          }),
        });
        if (!res.ok) {
          report.failures.push(`„${body.relationType}" konnte nicht geändert werden (${res.status}).`);
        } else {
          report.patched++;
        }
      } catch {
        report.failures.push(`„${body.relationType}" konnte nicht geändert werden (keine Verbindung).`);
      }
    }
  }

  // (2) Neu hinzugefügte Einträge über den bestehenden Anlage-Pfad.
  const isNew = (i: any) => relationIdsOf(i).length === 0;
  const fresh: RelationCollections = {
    gfg: (collections.gfg ?? []).filter(isNew),
    verant: (collections.verant ?? []).filter(isNew),
    firmaConn: (collections.firmaConn ?? []).filter(isNew),
    household: (collections.household ?? []).filter(isNew),
    weitere: (collections.weitere ?? []).filter(isNew),
    assistenz: (collections.assistenz ?? []).filter(isNew),
    personFirma: (collections.personFirma ?? []).filter(isNew),
    vertretung: (collections.vertretung ?? []).filter(isNew),
  };
  const hasFresh = Object.values(fresh).some((arr) => (arr?.length ?? 0) > 0);
  if (hasFresh) {
    await persistCollectedRelations(mainId, fresh, ownership, { ...opts, failures: report.failures });
  }

  // (3) In der Maske entfernte Beziehungen löschen.
  for (const id of previousRelationIds) {
    if (keptIds.has(id)) continue;
    try {
      const res = await fetch(apiUrl(`contact-relation/${id}`), { method: "DELETE" });
      // 404 = die Zeile ist bereits weg (z. B. durch die Schwester-Bereinigung
      // einer zuvor gelöschten Tochterfirma-Kante) — das ist das Ziel, kein Fehler.
      if (res.ok || res.status === 404) report.deleted++;
      else report.failures.push(`Eine Beziehung konnte nicht entfernt werden (${res.status}).`);
    } catch {
      report.failures.push("Eine Beziehung konnte nicht entfernt werden (keine Verbindung).");
    }
  }

  return report;
}
